import { ScrollArea } from "@/components/ui/scroll-area";
import { useState, useMemo } from "react";
import { Filter, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import DocumentFilterTabs from "./DocumentFilterTabs";
import SourceDocumentList from "./SourceDocumentList";
import type { SourceDocument } from "@/components/types";
import { cn } from "@/lib/utils";

interface SourceDocumentsMainSidebarProps {
  enrichedSourceDocuments: SourceDocument[];
  onDocumentNudge: (documentId: string) => void;
  nudgedDocumentId: string | null;
  className?: string;
}

const SourceDocumentsMainSidebar = ({
  enrichedSourceDocuments,
  nudgedDocumentId,
  className,
}: SourceDocumentsMainSidebarProps) => {
  const [selectedDocType, setSelectedDocType] = useState<string>("סימוכין");

  const handleFilterClick = () => {
    console.log("Filter clicked");
  };

  const handleSearchClick = () => {
    console.log("Search clicked");
  };

  const handleTabClick = (tabName: string) => {
    setSelectedDocType(tabName);
  };

  // Filter source documents based on selected tab
  const filteredSourceDocuments = useMemo(() => {
    if (!enrichedSourceDocuments) return [];

    switch (selectedDocType) {
      case "סימוכין":
        // Show only documents that are referenced in the answer (isAnswerRef === true)
        return enrichedSourceDocuments.filter((doc) => doc.metadata?.isAnswerRef === true);
      case "פסיקה רלוונטית":
        // Show all source documents
        return enrichedSourceDocuments;
      case "חקיקה רלוונטית":
        // Show all source documents (same as פסיקה רלוונטית for now)
        return enrichedSourceDocuments;
      default:
        return enrichedSourceDocuments;
    }
  }, [enrichedSourceDocuments, selectedDocType]);

  return (
    <ScrollArea className={cn("h-full w-3/7", className)}>
      {/* STICKY COOL Header */}
      <div
        className="sticky top-4 z-50 my-8 mx-2 max-md:my-4 max-md:p-3 bg-background rounded-lg shadow-lg border border-slate-200"
        dir="rtl"
      >
        {/* Navigation Bar with Icons and Tabs */}
        <div className="flex min-w-0 items-center justify-between gap-2 p-4 max-md:p-3">
          {/* Right Side - Tabs */}
          <DocumentFilterTabs selectedDocType={selectedDocType} onTabClick={handleTabClick} />

          {/* Left Side - Icons */}
          <div className="flex shrink-0 items-center gap-3">
            <Button variant="ghost" size="sm" onClick={handleFilterClick} className="p-2 hover:bg-slate-100" aria-label="סינון">
              <Filter className="w-4 h-4 text-slate-600" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSearchClick} className="p-2 hover:bg-slate-100" aria-label="חיפוש">
              <Search className="w-4 h-4 text-slate-600" />
            </Button>
          </div>
        </div>
      </div>

      <SourceDocumentList
        filteredSourceDocuments={filteredSourceDocuments}
        selectedDocType={selectedDocType}
        nudgedDocumentId={nudgedDocumentId}
      />
    </ScrollArea>
  );
};

export default SourceDocumentsMainSidebar;
